import { createSelector } from 'reselect';
import isNil from 'ramda/src/isNil';
import DateTimeService from 'mangools-commons/dist/services/DateTimeService';
import Defaults from 'mangools-commons/dist/constants/Defaults';

import UserPlanTypes from 'constants/UserPlanTypes';
import { trackedKeywordsCountSelector, trackingsCountSelector } from 'selectors/commonSelectors';

export const accessTokenSelector = state => state.user.accessToken;
export const createdAtSelector = state => state.user.createdAt;
export const fetchedSelector = state => state.user.fetched;
export const idSelector = state => state.user.id;
export const emailSelector = state => state.user.email;
export const limitsFetchedSelector = state => state.user.limits.fetched;
export const loggedInSelector = state => state.user.loggedIn;
export const ssoTicketSelector = state => state.user.ssoTicket;
export const trackedKeywordLimitSelector = state => state.user.limits.trackedKeywordLimit;
export const trackingsLimitSelector = state => state.user.limits.trackings;
export const userPlanSelector = state => state.user.plan;
export const loginTokenSelector = state => state.user.loginToken;
export const unleashSessionSelector = state => state.user.unleashSession;
export const isSubUserSelector = state => state.user.isSubUser;
export const settingsSelector = state => state.user.settings;

export const remainingTrackedKeywordLimitSelector = createSelector(
    [trackedKeywordsCountSelector, trackedKeywordLimitSelector],
    (trackedCount, limit) => {
        const remaining = limit - trackedCount;
        return remaining < 0 ? 0 : remaining;
    },
);

export const remainingTrackingsLimitSelector = createSelector(
    [trackingsCountSelector, trackingsLimitSelector],
    (trackingsCount, limit) => {
        const remaining = limit - trackingsCount;
        return remaining < 0 ? 0 : remaining;
    },
);

export const userPlanTypeSelector = createSelector([loggedInSelector, userPlanSelector], (loggedIn, userPlan) => {
    if (loggedIn === false || isNil(userPlan)) {
        return UserPlanTypes.NO_PLAN;
    } else {
        switch (userPlan) {
            case 'free': {
                return UserPlanTypes.FREE;
            }
            case 'trial': {
                return UserPlanTypes.TRIAL;
            }
            case 'entry': {
                return UserPlanTypes.ENTRY;
            }
            case 'basic': {
                return UserPlanTypes.BASIC;
            }
            case 'premium': {
                return UserPlanTypes.PREMIUM;
            }
            case 'agency': {
                return UserPlanTypes.AGENCY;
            }
            case 'ai_premium': {
                return UserPlanTypes.AI_PREMIUM;
            }
            case 'custom': {
                return UserPlanTypes.CUSTOM;
            }
            default: {
                // NOTE: Should never happen
                return UserPlanTypes.NO_PLAN;
            }
        }
    }
});

export const isWithUpsellModalForNewTrackingLocation = createSelector(userPlanTypeSelector, userPlan => {
    return [
        UserPlanTypes.NO_PLAN,
        UserPlanTypes.CUSTOM,
        UserPlanTypes.FREE,
        UserPlanTypes.TRIAL,
        UserPlanTypes.ENTRY,
    ].includes(userPlan);
});

export const newUserDiscountTimerLimitSelector = createSelector(
    [loggedInSelector, createdAtSelector, userPlanTypeSelector],
    (loggedIn, createdAt, userPlanType) => {
        // TODO: remove after debugging
        // return 15 * 60;

        // Only trial user can see this subscribe message, every new user will be TRIAL user
        if (loggedIn && !isNil(createdAt) && userPlanType === UserPlanTypes.TRIAL) {
            return DateTimeService.getTimeDifferenceInSeconds(
                createdAt + Defaults.USER_FRESH_LIMIT_MINUTES * 60 * 1000,
            );
        } else {
            return 0;
        }
    },
);

// Black Friday ends: Dec 10, 2025, 23:59:59 PST (UTC-8)
const BLACK_FRIDAY_END_DATE = new Date('2025-12-11T07:59:59Z'); // Dec 11 07:59:59 UTC = Dec 10 23:59:59 PST

export const newUserDiscountActiveSelector = createSelector(
    [loggedInSelector, createdAtSelector, userPlanTypeSelector],
    (loggedIn, createdAt, userPlanType) => {
        // TODO: remove after debugging
        // return true;

        const isBlackFriday = new Date() < BLACK_FRIDAY_END_DATE;

        if (loggedIn && !isNil(createdAt)) {
            if (isBlackFriday) {
                // During Black Friday: show for both FREE and TRIAL users (no time limit)
                return userPlanType === UserPlanTypes.FREE || userPlanType === UserPlanTypes.TRIAL;
            } else {
                // After Black Friday: only trial users with time limit
                if (userPlanType === UserPlanTypes.TRIAL) {
                    return DateTimeService.lessThanMinutesAgo(Defaults.USER_FRESH_LIMIT_MINUTES, createdAt);
                }
            }
        }
        return false;
    },
);

export const whiteLabelSettingsSelector = state => state.user.settings.whiteLabel;
export const fetchingToggleWhitelabelSelector = state => state.user.fetchingWhitelabelToggle;
export const errorToggleWhitelabelStatusSelector = state => state.user.errorToggleWhitelabel.status;

// Looker Studio Token selectors
export const userLookerStudioTokenSelector = state => state.user.lookerStudio.lookerStudioToken;
export const fetchingUserLookerStudioTokenSelector = state => state.user.lookerStudio.fetching;
export const errorUserLookerStudioTokenSelector = state => state.user.lookerStudio.error;
